import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";
import { Icon, type IconName } from "../atoms/icon.js";

const toastVariants = cva(
    "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all",
    {
        variants: {
            variant: {
                default: "border bg-background text-foreground",
                destructive: "destructive border-destructive bg-destructive text-destructive-foreground",
                success: "border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-50",
                warning: "border-yellow-200 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-50",
                info: "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-50",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    }
);

const iconMapping: Record<string, IconName> = {
    default: "info",
    destructive: "x-circle",
    success: "check-circle",
    warning: "alert-circle",
    info: "info",
};

export interface ToastProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof toastVariants> {
    title?: string;
    description?: string;
    action?: React.ReactNode;
    onClose?: () => void;
    duration?: number;
    showIcon?: boolean;
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
    ({
        className,
        variant = "default",
        title,
        description,
        action,
        onClose,
        duration = 5000,
        showIcon = true,
        ...props
    }, ref) => {
        const [isVisible, setIsVisible] = React.useState(true);

        React.useEffect(() => {
            if (duration && duration > 0) {
                const timer = setTimeout(() => {
                    setIsVisible(false);
                    setTimeout(() => onClose?.(), 300); // Allow fade out animation
                }, duration);

                return () => clearTimeout(timer);
            }
        }, [duration, onClose]);

        if (!isVisible) {
            return null;
        }

        const iconName = iconMapping[variant || "default"];

        return (
            <div
                ref={ref}
                className={cn(toastVariants({ variant }), className)}
                {...props}
            >
                <div className="flex items-start gap-3">
                    {showIcon && iconName && (
                        <Icon
                            name={iconName}
                            size="sm"
                            className="mt-0.5 flex-shrink-0"
                        />
                    )}
                    <div className="grid gap-1">
                        {title && (
                            <div className="text-sm font-semibold">{title}</div>
                        )}
                        {description && (
                            <div className="text-sm opacity-90">{description}</div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {action}
                    {onClose && (
                        <button
                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent opacity-70 hover:opacity-100 focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100"
                            onClick={() => {
                                setIsVisible(false);
                                setTimeout(() => onClose(), 300);
                            }}
                        >
                            <Icon name="close" size="sm" />
                        </button>
                    )}
                </div>
            </div>
        );
    }
);

Toast.displayName = "Toast";

// Toast context and hook for easy usage
interface ToastContextValue {
    toasts: ToastProps[];
    addToast: (toast: Omit<ToastProps, 'onClose'>) => void;
    removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
    const context = React.useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export interface ToastProviderProps {
    children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [toasts, setToasts] = React.useState<(ToastProps & { id: string })[]>([]);

    const addToast = React.useCallback((toast: Omit<ToastProps, 'onClose'>) => {
        const id = Math.random().toString(36).substr(2, 9);
        setToasts(prev => [...prev, { ...toast, id }]);
    }, []);

    const removeToast = React.useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
            {children}
            <div className="fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]">
                {toasts.map(({ id, ...toast }) => (
                    <Toast
                        key={id}
                        {...toast}
                        onClose={() => removeToast(id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};
